'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getStoredUserRaw } from '@/lib/authStorage';
import {
  mrApiErrorMessage,
  mrApiLobbyFriendsList,
  mrApiLobbyJoin,
  mrApiLobbyLeave,
  mrApiLobbyPoll,
  mrApiLobbyPublicList,
  mrApiLobbyReady,
  mrApiLobbySlot,
  mrApiQueueJoin,
  type MrLobbyBrowserEntry,
  type MrLobbyResponse,
} from './runtime/net/mysteryRoomApi';

type Props = {
  /** Полноэкранный режим со страницы игры */
  onBack?: () => void;
  /** Переключение на экран создания лобби */
  onOpenCreateLobby?: () => void;
  /** Встроено в оверлей «Поиск игры» на странице лобби */
  embedded?: boolean;
  /** Передать вошедшее лобби родительскому экрану создания */
  onJoinedLobby?: (lobby: NonNullable<MrLobbyResponse['lobby']>) => void;
};

type JoinTab = 'friends' | 'public';

/** Человекочитаемый статус для таблицы поиска лобби */
export function joinBrowserSessionLabel(entry: MrLobbyBrowserEntry): { badge: string; line: string } {
  const phase = entry.matchPhase?.toLowerCase()?.trim() || '';
  if (phase === 'live') return { badge: 'Игра', line: 'Матч идёт' };
  if (phase && phase !== 'lobby' && ['briefing', 'assignment', 'bidding', 'accusation', 'result'].includes(phase)) {
    return { badge: 'Игра', line: `Матч · фаза ${phase}` };
  }
  const st = (entry.lobbyStatus ?? 'open').toLowerCase();
  if (st === 'ready') return { badge: 'Лобби', line: 'Сбор завершён, ждём старт' };
  if (st === 'starting') return { badge: 'Лобби', line: 'Запуск матча…' };
  return { badge: 'Лобби', line: 'Комната создана, набор игроков' };
}

function readStoredUserId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = getStoredUserRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { id?: unknown };
    const id = Number(parsed?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export default function MysteryRoomJoinBrowser({ onBack, onOpenCreateLobby, embedded = false, onJoinedLobby }: Props) {
  const [tab, setTab] = useState<JoinTab>('friends');
  const [friendsLobbies, setFriendsLobbies] = useState<MrLobbyBrowserEntry[]>([]);
  const [publicLobbies, setPublicLobbies] = useState<MrLobbyBrowserEntry[]>([]);
  const [status, setStatus] = useState('Загрузка списка лобби...');
  const [busyLobbyId, setBusyLobbyId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [officialOnly, setOfficialOnly] = useState(false);
  const [maxPing, setMaxPing] = useState(150);
  const [joinCode, setJoinCode] = useState('');
  const [activeLobby, setActiveLobby] = useState<MrLobbyResponse['lobby'] | null>(null);
  const [roomStatus, setRoomStatus] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ author: string; text: string; ts: number; mine?: boolean; system?: boolean }>>(() => [
    { author: 'Система', text: 'Выберите лобби, чтобы войти в комнату хоста.', ts: Date.now(), system: true },
  ]);
  const userId = useMemo(() => readStoredUserId(), []);

  const loadLists = useCallback(async () => {
    try {
      const [friends, pub] = await Promise.all([mrApiLobbyFriendsList(40), mrApiLobbyPublicList(60)]);
      setFriendsLobbies(friends.lobbies ?? []);
      setPublicLobbies(pub.lobbies ?? []);
      setStatus('Лобби обновлены');
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось загрузить список лобби'));
    }
  }, []);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  useEffect(() => {
    const timer = window.setInterval(() => void loadLists(), 10000);
    return () => window.clearInterval(timer);
  }, [loadLists]);

  useEffect(() => {
    if (!activeLobby?.id) return;
    const poll = async () => {
      try {
        const response = await mrApiLobbyPoll(activeLobby.id);
        setActiveLobby(response.lobby);
      } catch {
        // лобби могло закрыться
      }
    };
    const timer = window.setInterval(() => void poll(), 3500);
    return () => window.clearInterval(timer);
  }, [activeLobby?.id]);

  const filteredLobbies = useMemo(() => {
    const src = tab === 'friends' ? friendsLobbies : publicLobbies;
    const needle = query.trim().toLowerCase();
    return src.filter((entry) => {
      if (officialOnly && !entry.isOfficialMap) return false;
      if (entry.pingHint > maxPing) return false;
      if (!needle) return true;
      const haystack = `${entry.mapTitle} ${entry.ownerName} ${entry.gameMode} ${entry.region} ${entry.lobbyCode ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [friendsLobbies, maxPing, officialOnly, publicLobbies, query, tab]);

  const myPlayer = useMemo(() => activeLobby?.players.find((p) => p.userId === userId) ?? null, [activeLobby?.players, userId]);
  const mySlot = myPlayer?.slotIndex ?? null;
  const myIsReady = Boolean(myPlayer?.isReady);
  const unassigned = activeLobby?.unassignedPlayers ?? [];
  const slots = activeLobby?.slots ?? [];

  const joinByCode = useCallback(async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) {
      setStatus('Введите код лобби');
      return;
    }
    try {
      const response = await mrApiLobbyJoin({ lobbyCode: code });
      setActiveLobby(response.lobby);
      if (response.lobby && onJoinedLobby) {
        onJoinedLobby(response.lobby);
      }
      setChatMessages((cur) => [...cur, { author: 'Система', text: 'Вы вошли в лобби хоста.', ts: Date.now(), system: true }]);
      setRoomStatus('Вы вошли в лобби. Сначала выберите слот.');
      setStatus('Подключено к лобби');
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось войти по коду'));
    }
  }, [joinCode, onJoinedLobby]);

  const joinEntry = useCallback(async (entry: MrLobbyBrowserEntry) => {
    setBusyLobbyId(entry.lobbyId);
    try {
      const response = await mrApiLobbyJoin({ lobbyId: entry.lobbyId });
      setActiveLobby(response.lobby);
      if (response.lobby && onJoinedLobby) {
        onJoinedLobby(response.lobby);
      }
      setChatMessages((cur) => [...cur, { author: 'Система', text: `Вы вошли в лобби ${entry.ownerName}.`, ts: Date.now(), system: true }]);
      setRoomStatus('Вы в комнате лобби. Если вы без слота — займите место.');
      setStatus(`Вход в лобби: ${entry.mapTitle}`);
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось войти в лобби'));
    } finally {
      setBusyLobbyId(null);
    }
  }, [onJoinedLobby]);

  const takeSlot = useCallback(async (slotIndex: number) => {
    if (!activeLobby) return;
    try {
      const response = await mrApiLobbySlot({ lobbyId: activeLobby.id, slotIndex, action: 'take' });
      setActiveLobby(response.lobby);
      setRoomStatus(`Слот #${slotIndex + 1} занят`);
    } catch (error: any) {
      setRoomStatus(mrApiErrorMessage(error, 'Не удалось занять слот'));
    }
  }, [activeLobby]);

  const releaseSlot = useCallback(async () => {
    if (!activeLobby) return;
    try {
      const response = await mrApiLobbySlot({ lobbyId: activeLobby.id, action: 'leave' });
      setActiveLobby(response.lobby);
      setRoomStatus('Вы снова в статусе «неопределившийся»');
    } catch (error: any) {
      setRoomStatus(mrApiErrorMessage(error, 'Не удалось освободить слот'));
    }
  }, [activeLobby]);

  const toggleReady = useCallback(async () => {
    if (!activeLobby || userId === null) return;
    try {
      const response = await mrApiLobbyReady({ lobbyId: activeLobby.id, ready: !myIsReady });
      setActiveLobby(response.lobby);
      setRoomStatus(!myIsReady ? 'Готовность подтверждена' : 'Готовность снята');
    } catch (error: any) {
      setRoomStatus(mrApiErrorMessage(error, 'Не удалось изменить готовность'));
    }
  }, [activeLobby, myIsReady, userId]);

  const leaveLobby = useCallback(async () => {
    if (!activeLobby) return;
    try {
      await mrApiLobbyLeave({ lobbyId: activeLobby.id });
      setActiveLobby(null);
      setRoomStatus('');
      setChatMessages((cur) => [...cur, { author: 'Система', text: 'Вы вышли из лобби.', ts: Date.now(), system: true }]);
      setStatus('Вы вышли из лобби');
      void loadLists();
    } catch (error: any) {
      setRoomStatus(mrApiErrorMessage(error, 'Не удалось выйти из лобби'));
    }
  }, [activeLobby, loadLists]);

  const quickQueue = useCallback(async () => {
    try {
      const response = await mrApiQueueJoin();
      if (response.status === 'matched') {
        setStatus(`Матч найден: #${response.matchId ?? '—'}`);
      } else {
        setStatus(`Поиск игроков: ${response.queue?.players ?? 1}/${response.queue?.requiredPlayers ?? 4}`);
      }
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось запустить быстрый поиск'));
    }
  }, []);

  const shellCls = embedded
    ? 'flex h-full min-h-0 w-full flex-col gap-3'
    : 'mx-auto flex h-full min-h-0 w-full max-w-[1800px] flex-col gap-3 px-4 py-4';

  const sendLobbyChat = useCallback(() => {
    const text = chatInput.trim();
    if (!text) return;
    const author = myPlayer?.displayName || 'Вы';
    setChatMessages((cur) => [...cur, { author, text, ts: Date.now(), mine: true }]);
    setChatInput('');
  }, [chatInput, myPlayer?.displayName]);

  return (
    <div className={shellCls}>
      {!embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[14px] border border-site-border bg-black/35 px-4 py-3">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.2em] text-site-muted">Мультиплеер</div>
            <div className="text-lg font-black text-site-text">Присоединиться к игре</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={quickQueue} className="rounded-lg border border-site-success/35 bg-site-success/15 px-3 py-2 text-xs font-semibold text-site-success">
              Быстрый поиск
            </button>
            <button type="button" onClick={onOpenCreateLobby} className="rounded-lg border border-site-accent/35 bg-site-accent/15 px-3 py-2 text-xs font-semibold text-site-accent">
              Создать лобби
            </button>
            <button type="button" onClick={onBack} className="rounded-lg border border-site-border bg-site-surface/[0.04] px-3 py-2 text-xs font-semibold text-site-text">
              Назад
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
          <button type="button" onClick={quickQueue} className="rounded-lg border border-site-success/35 bg-site-success/15 px-3 py-2 text-xs font-semibold text-site-success">
            Быстрый поиск
          </button>
          {onOpenCreateLobby ? (
            <button type="button" onClick={onOpenCreateLobby} className="rounded-lg border border-site-accent/35 bg-site-accent/15 px-3 py-2 text-xs font-semibold text-site-accent">
              Настройка лобби
            </button>
          ) : null}
        </div>
      )}

      {activeLobby ? (
        <section className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(320px,0.9fr)_minmax(520px,1.4fr)_minmax(280px,0.8fr)]">
          <aside className="min-h-0 rounded-[14px] border border-site-border bg-black/35 p-3">
            <div className="text-[0.66rem] uppercase tracking-[0.16em] text-site-accent/80">Лобби хоста</div>
            <div className="mt-1 text-lg font-black text-site-text">Комната #{activeLobby.id}</div>
            <div className="mt-1 text-xs text-site-muted">
              Код {activeLobby.lobbyCode ?? '—'} · {activeLobby.isPrivate ? 'приватное' : 'открытое'}
            </div>
            <div className="mt-3 rounded-lg border border-site-border bg-site-bg/75 p-3 text-xs text-site-secondary">
              <div className="font-semibold text-site-text">Настройки матча</div>
              <div className="mt-2 grid gap-1.5">
                <ReadOnlyRow label="Карта" value={activeLobby.mapId ?? '—'} />
                <ReadOnlyRow label="Режим" value={String(activeLobby.settings?.gameMode ?? 'classic')} />
                <ReadOnlyRow label="Сложность" value={String(activeLobby.settings?.botDifficulty ?? 'normal')} />
                <ReadOnlyRow label="Инциденты" value={String(activeLobby.settings?.incidentLevel ?? 'standard')} />
                <ReadOnlyRow label="Игроки" value={`${activeLobby.players.length}/${activeLobby.requiredPlayers}`} />
                <ReadOnlyRow label="Статус" value={activeLobby.status} />
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-site-border bg-site-bg/75 p-3">
              <div className="text-[0.66rem] uppercase tracking-[0.12em] text-site-muted">Неопределившиеся</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {unassigned.length > 0 ? (
                  unassigned.map((p) => (
                    <span key={`${p.userId}-${p.joinedAt}`} className="rounded-full border border-site-warning/30 bg-site-warning/10 px-2.5 py-1 text-[0.72rem] text-site-warning">
                      {p.displayName}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-site-muted">Нет</span>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => void toggleReady()} className="rounded-lg border border-site-success/35 bg-site-success/15 px-3 py-2 text-xs font-semibold text-site-success">
                {myIsReady ? 'Снять готовность' : 'Готов'}
              </button>
              {mySlot !== null ? (
                <button type="button" onClick={() => void releaseSlot()} className="rounded-lg border border-site-warning/35 bg-site-warning/15 px-3 py-2 text-xs font-semibold text-site-warning">
                  Покинуть слот
                </button>
              ) : null}
              <button type="button" onClick={() => void leaveLobby()} className="rounded-lg border border-site-danger/35 bg-site-danger/15 px-3 py-2 text-xs font-semibold text-site-danger">
                Выйти из лобби
              </button>
            </div>
            <div className="mt-2 text-xs text-site-muted">{roomStatus || 'Займите слот и нажмите «Готов».'}</div>
          </aside>

          <section className="min-h-0 rounded-[14px] border border-site-border bg-black/35 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-site-text">Слоты игроков</div>
                <div className="mt-0.5 text-[0.68rem] text-site-muted">Изменения хоста обновляются автоматически</div>
              </div>
              <span className="rounded-full border border-site-border bg-site-bg/80 px-2.5 py-1 text-[0.68rem] font-semibold text-site-secondary">
                {activeLobby.players.length}/{activeLobby.requiredPlayers}
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {slots.map((slot) => {
                const isMySlot = mySlot !== null && slot.index === mySlot;
                const canTake = slot.status === 'open' || slot.status === 'bot';
                return (
                  <div key={slot.index} className={`rounded-lg border p-2 ${isMySlot ? 'border-site-accent/45 bg-site-accent/10' : 'border-site-border bg-site-bg/75'}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-site-muted">Слот {slot.index + 1}</span>
                      <span className="text-site-muted">{slot.status}</span>
                    </div>
                    <div className="mt-1 text-sm text-site-text">{slot.player?.displayName ?? 'Свободно'}</div>
                    <div className="mt-0.5 text-[0.68rem] text-site-muted">
                      {slot.player ? (slot.player.isReady ? 'Готов' : 'Не готов') : 'Ожидает игрока'}
                    </div>
                    {canTake && !slot.player?.isBot ? (
                      <button type="button" onClick={() => void takeSlot(slot.index)} className="mt-2 rounded-md border border-site-accent/35 bg-site-accent/15 px-2 py-1 text-[0.68rem] font-semibold text-site-accent">
                        Занять
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>

          <aside className="flex min-h-0 flex-col rounded-[14px] border border-site-border bg-black/35 p-3">
            <div className="mb-2 text-sm font-semibold text-site-text">Чат лобби</div>
            <div className="min-h-0 flex-1 space-y-2 overflow-auto rounded-lg border border-site-border bg-site-bg/60 p-2">
              {chatMessages.map((message, index) => (
                <div key={`${message.ts}-${index}`} className={`rounded-lg px-2 py-1.5 text-xs ${message.system ? 'bg-site-warning/10 text-site-warning' : message.mine ? 'bg-site-accent/12 text-site-accent' : 'bg-site-surface/[0.04] text-site-text'}`}>
                  <span className="font-semibold">{message.author}: </span>
                  {message.text}
                  <span className="ml-2 text-[0.62rem] text-site-muted">{formatLobbyChatTs(message.ts)}</span>
                </div>
              ))}
            </div>
            <form
              className="mt-2 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                sendLobbyChat();
              }}
            >
              <input
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                placeholder="Сообщение..."
                className="min-w-0 flex-1 rounded-lg border border-site-border bg-site-bg/80 px-3 py-2 text-sm text-site-text placeholder:text-site-muted"
              />
              <button type="submit" className="rounded-lg border border-site-accent/35 bg-site-accent/15 px-3 py-2 text-xs font-semibold text-site-accent">
                Отправить
              </button>
            </form>
          </aside>
        </section>
      ) : (
      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(320px,1fr)_minmax(520px,1.5fr)]">
        <section className="min-h-0 rounded-[14px] border border-site-border bg-black/35 p-3">
          <div className="mb-2 flex rounded-xl border border-site-border bg-site-bg/60 p-1 text-xs font-semibold">
            <button type="button" className={`flex-1 rounded-lg px-2 py-1.5 ${tab === 'friends' ? 'bg-site-accent text-site-on-accent' : 'text-site-secondary'}`} onClick={() => setTab('friends')}>Лобби друзей</button>
            <button type="button" className={`flex-1 rounded-lg px-2 py-1.5 ${tab === 'public' ? 'bg-site-accent text-site-on-accent' : 'text-site-secondary'}`} onClick={() => setTab('public')}>Открытые</button>
          </div>
          <div className="space-y-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Фильтр: карта, владелец, код..." className="w-full rounded-lg border border-site-border bg-site-bg/80 px-3 py-2 text-sm text-site-text placeholder:text-site-muted" />
            <div className="flex items-center justify-between gap-2 text-xs text-site-secondary">
              <label className="inline-flex items-center gap-2"><input type="checkbox" checked={officialOnly} onChange={(e) => setOfficialOnly(e.target.checked)} />Только официальные</label>
              <span>Ping ≤ {maxPing} ms</span>
            </div>
            <input type="range" min={30} max={220} step={5} value={maxPing} onChange={(e) => setMaxPing(Number(e.target.value))} className="w-full" />
          </div>
          <div className="mt-3 rounded-lg border border-dashed border-site-border bg-site-bg/40 px-3 py-3 text-[0.72rem] leading-snug text-site-muted">
            Результаты — в таблице справа. Здесь фильтры и вход по коду. В списке только лобби на сервере (комната создаётся при регистрации лобби у хоста, не от одного открытия меню без подключения).
          </div>
          <div className="mt-3 rounded-lg border border-site-border bg-site-bg/70 p-2">
            <div className="text-[0.66rem] uppercase tracking-[0.12em] text-site-muted">Вход по коду</div>
            <div className="mt-2 flex gap-2">
              <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="ABCDE" className="min-w-0 flex-1 rounded-lg border border-site-border bg-site-bg/80 px-3 py-2 text-sm text-site-text placeholder:text-site-muted" />
              <button type="button" onClick={() => void joinByCode()} className="rounded-lg border border-site-warning/35 bg-site-warning/15 px-3 py-2 text-xs font-semibold text-site-warning">Войти</button>
            </div>
          </div>
          <div className="mt-2 text-xs text-site-muted">{status}</div>
        </section>

        <section className="flex min-h-0 flex-col rounded-[14px] border border-site-border bg-black/35 p-3">
          <div className="mb-2 flex flex-wrap items-end justify-between gap-2 border-b border-site-border pb-2">
            <div>
              <div className="text-sm font-semibold text-site-text">Найденные лобби</div>
              <div className="mt-0.5 text-[0.68rem] text-site-muted">Игроки · статус лобби или матча · действие</div>
            </div>
            <span className="rounded-full border border-site-border bg-site-bg/80 px-2.5 py-1 text-[0.68rem] font-semibold text-site-secondary">
              {filteredLobbies.length} шт.
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-site-border bg-site-bg/50">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead className="sticky top-0 z-[1] bg-site-bg/98 text-[0.62rem] font-bold uppercase tracking-[0.08em] text-site-muted shadow-[0_1px_0_rgba(255,255,255,0.08)]">
                <tr>
                  <th className="whitespace-nowrap px-2 py-2 pl-3">Карта</th>
                  <th className="whitespace-nowrap px-2 py-2">Хост</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Игроки</th>
                  <th className="whitespace-nowrap px-2 py-2">Тип</th>
                  <th className="whitespace-nowrap px-2 py-2">Статус</th>
                  <th className="whitespace-nowrap px-2 py-2">Код</th>
                  <th className="whitespace-nowrap px-2 py-2 pr-3 text-right"> </th>
                </tr>
              </thead>
              <tbody className="text-[0.74rem] text-site-text">
                {filteredLobbies.map((entry) => {
                  const { badge, line } = joinBrowserSessionLabel(entry);
                  const active = activeLobby?.id === entry.lobbyId;
                  return (
                    <tr
                      key={`${entry.lobbyId}-${entry.updatedAt}`}
                      className={`border-t border-site-border/[0.06] transition-colors ${active ? 'bg-site-accent/12' : 'hover:bg-site-surface/[0.04]'}`}
                    >
                      <td className="max-w-[200px] px-2 py-2 pl-3 align-top">
                        <div className="truncate font-semibold text-site-text" title={entry.mapTitle}>
                          {entry.mapTitle || entry.mapId}
                        </div>
                        <div className="mt-0.5 truncate text-[0.65rem] text-site-muted" title={entry.gameMode}>
                          {entry.gameMode} · {entry.region} · ~{entry.pingHint} ms
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 align-top text-site-secondary">{entry.ownerName}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-center align-top tabular-nums font-semibold text-site-text">
                        {entry.players}/{entry.maxPlayers}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 align-top">
                        <span
                          className={`inline-block rounded-md px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide ${
                            badge === 'Игра' ? 'bg-site-success/20 text-site-success' : 'bg-site-warning/15 text-site-warning'
                          }`}
                        >
                          {badge}
                        </span>
                      </td>
                      <td className="max-w-[220px] px-2 py-2 align-top text-[0.68rem] leading-snug text-site-muted" title={line}>
                        {line}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 align-top font-mono text-[0.68rem] text-site-muted">
                        {entry.lobbyCode ?? '—'}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 pr-3 text-right align-top">
                        <button
                          type="button"
                          onClick={() => void joinEntry(entry)}
                          disabled={busyLobbyId === entry.lobbyId}
                          className="rounded-lg border border-site-accent/40 bg-site-accent/15 px-2.5 py-1.5 text-[0.68rem] font-semibold text-site-accent disabled:opacity-45"
                        >
                          Войти
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredLobbies.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-[0.78rem] text-site-muted">
                      Лобби по текущим фильтрам не найдены. Смените вкладку, ослабьте фильтр ping или дождитесь, пока друг создаст комнату на сервере.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-[0.72rem] leading-relaxed text-site-muted">
            Нажмите «Войти» в строке таблицы или используйте код слева. После входа откроется вкладка лобби хоста.
          </p>
        </section>
      </div>
      )}
    </div>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-site-muted">{label}</span>
      <span className="min-w-0 truncate text-right text-site-text" title={value}>{value}</span>
    </div>
  );
}

function formatLobbyChatTs(ts: number): string {
  return new Date(ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

